import React, { useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { Trans, useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments } from '@modernloop/shared/components';
import { getCurrentDateIso, isAfterNow, isBeforeNow, isWithinInterval } from '@modernloop/shared/datetime';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { Divider, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  CancelScheduleDialog_ApplicationStageFragment,
  CancelScheduleDialog_InterviewsFragment,
} from 'src/generated/mloop-graphql';

import AtsName from 'src/entities/Ats/AtsName';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useDirectoryInfo from 'src/hooks/useDirectoryInfo';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import cancelSchedule, { DeleteType } from 'src/utils/api/cancelSchedule';

type Props = {
  onSuccess: () => void;
  onError: () => void;
  onClose: () => void;
};

type Fragments = {
  applicationStage: CancelScheduleDialog_ApplicationStageFragment;
  interviews: CancelScheduleDialog_InterviewsFragment[];
};

const CancelScheduleDialog: FCWithFragments<Fragments, Props> = ({
  applicationStage,
  interviews,
  onSuccess,
  onError,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const directoryInfo = useDirectoryInfo();

  const refetchActivityFeed = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();

  const getInitialDeleteType = () => {
    if (isAfterNow(applicationStage?.minStartAt)) {
      return DeleteType.UPCOMING;
    }
    if (isBeforeNow(applicationStage?.maxEndAt)) {
      return DeleteType.PAST;
    }
    const isUpcomingInterview = interviews?.find((interview) => !!isAfterNow(interview?.startAt));
    if (!isUpcomingInterview) {
      return DeleteType.ALL;
    }
    return DeleteType.UPCOMING;
  };

  const [deleteType, setDeleteType] = useState<DeleteType>(getInitialDeleteType());
  const [canceling, setCanceling] = useState<boolean>(false);

  const handleDeleteTypeChange = (event: React.SyntheticEvent, value: DeleteType) => {
    setDeleteType(value);
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      await cancelSchedule({ applicationStageId: applicationStage?.id, deleteType });
      refetchScheduleTaskQuery();
      refetchActivityFeed();
      enqueueSnackbar(t('hiring-dashboard-schedule:schedule.cancelDialog.scheduleCanceled'), { variant: 'success' });

      onSuccess();
    } catch (error) {
      const errorMsg =
        getExternalErrorMessage(error) || t('hiring-dashboard-schedule:schedule.cancelDialog.failedToCancel');
      enqueueSnackbar(errorMsg, { variant: 'error' });

      onError();
    }
  };

  const bodyCommonContent = useMemo((): JSX.Element => {
    return (
      <Stack direction="column" spacing={2}>
        <Typography>
          <Trans
            i18nKey="hiring-dashboard-schedule:schedule.cancelDialog.atsDeleteWarning"
            components={{ AtsName: <AtsName /> }}
          />
        </Typography>
        <Typography>
          {t('hiring-dashboard-schedule:schedule.cancelDialog.calendarDeleteWarning', {
            calendarName: directoryInfo.label,
          })}
        </Typography>
      </Stack>
    );
  }, [directoryInfo.label, t]);

  const getBody = (): JSX.Element => {
    if (
      isWithinInterval(getCurrentDateIso(), { start: applicationStage?.minStartAt, end: applicationStage?.maxEndAt })
    ) {
      const isUpcomingInterview = interviews?.find((interview) => !!isAfterNow(interview?.startAt));

      return (
        <Stack direction="column" spacing={2}>
          {isUpcomingInterview && (
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="deleteType"
                name="deleteType"
                value={deleteType}
                onChange={handleDeleteTypeChange}
              >
                <FormControlLabel
                  value={DeleteType.ALL}
                  control={<Radio color="primary" />}
                  label={t('hiring-dashboard-schedule:schedule.cancelDialog.allInterviews')}
                />
                <FormControlLabel
                  value={DeleteType.UPCOMING}
                  control={<Radio color="primary" />}
                  label={t('hiring-dashboard-schedule:schedule.cancelDialog.upcomingOnly')}
                />
              </RadioGroup>
            </FormControl>
          )}

          {isUpcomingInterview && <Divider />}

          {bodyCommonContent}
        </Stack>
      );
    }

    return bodyCommonContent;
  };

  return (
    <Dialog
      dataTestId="schedule-object-cancel-schedule-dialog"
      title={t('hiring-dashboard-schedule:schedule.cancelDialog.title')}
      subTitle={t('hiring-dashboard-schedule:schedule.cancelDialog.subtitle')}
      open
      onClose={onClose}
      submitOptions={{
        dataTestId: 'schedule-object-cancel-schedule-confirm-button',
        label: t('hiring-dashboard-schedule:schedule.cancelDialog.markAsCanceled'),
        isDisabled: canceling,
        isLoading: canceling,
        isDangerous: true,
        onClick: handleCancel,
      }}
      cancelOptions={{
        label: t('hiring-dashboard-schedule:schedule.cancelDialog.keepSchedule'),
        onClick: onClose,
      }}
    >
      {getBody()}
    </Dialog>
  );
};

CancelScheduleDialog.fragments = {
  applicationStage: gql`
    fragment CancelScheduleDialog_applicationStage on ApplicationStage {
      id
      minStartAt
      maxEndAt
    }
  `,
  interviews: gql`
    fragment CancelScheduleDialog_interviews on ApplicationStageInterview {
      id
      startAt
    }
  `,
};

export default CancelScheduleDialog;
